import { Pause, Play } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Electrolysis3D } from './Electrolysis3D';
import { ExploreChallenges } from './ExploreChallenges';
import { type ElectrolysisCase } from './electrochemicalCells';
import {
  DEFAULT_ELECTROLYSIS_SETUP,
  ELECTROLYSIS_FIELDS,
  FARADAY_CHALLENGES,
  FARADAY_QUESTION,
  MOLAR_GAS_VOLUME_L_PER_MOL,
  calculateElectrolysisExplore,
  sig3,
  type ElectrolysisSetup,
} from './electrochemicalExplore';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';
import { formatModelNumber } from './modelInputContract';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

/**
 * Faraday Explore: the learner types the current and time; the worked steps, readouts and the 3D
 * cell all come from one `calculateElectrolysisExplore` result. Invalid input keeps the last result.
 * The setup and the ticked tasks stay when the learner switches electrolyte, so cells can be compared.
 */
export function ElectrolysisExplore({ item }: { item: ElectrolysisCase }) {
  const [setup, setSetup] = useState<ElectrolysisSetup>(DEFAULT_ELECTROLYSIS_SETUP);
  const reducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState<boolean | null>(null);
  const moving = playing ?? !reducedMotion;
  const result = useMemo(() => calculateElectrolysisExplore(item, setup), [item, setup]);
  const product = item.faraday.productLabel;

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm font-medium leading-6 text-slate2">
          Type a current and a time. The model counts the charge, turns it into moles of electrons and then into {product}.
          Anode (+) on the left, cathode (−) on the right.
        </p>
        <button
          type="button"
          onClick={() => setPlaying(!moving)}
          className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
        >
          {moving ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
          {moving ? 'Pause bubbles' : 'Play bubbles'}
        </button>
      </div>

      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        <figure className="min-w-0">
          <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">What you see</figcaption>
          <Electrolysis3D
            state={result.scene}
            playing={moving}
            label={`Electrolysis of ${item.name} at ${formatModelNumber(setup.currentA, 2)} A for ${formatModelNumber(setup.timeMin, 0)} min: ${sig3(result.massG)} g of ${product} at the cathode`}
          />
          <dl className="mt-3 grid gap-2 sm:grid-cols-3" aria-label="Electrolysis readout">
            <div className="rounded-2xl border border-hairline bg-surface p-3">
              <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Charge</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink" data-testid="faraday-charge">
                {formatModelNumber(result.chargeC, 0)} C
              </dd>
            </div>
            <div className="rounded-2xl border border-hairline bg-surface p-3">
              <dt className="text-xs font-extrabold uppercase tracking-wide text-slate2">Electrons</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink">{sig3(result.electronMol)} mol</dd>
            </div>
            <div className="rounded-2xl border border-brand-coral/25 bg-wash-coral p-3">
              <dt className="text-xs font-extrabold uppercase tracking-wide text-brand-coral">Mass of {product}</dt>
              <dd className="mt-1 text-lg font-extrabold text-ink" data-testid="faraday-mass">
                {sig3(result.massG)} g
              </dd>
              {result.gasVolumeL !== null ? (
                <dd className="text-xs font-bold text-slate2">{sig3(result.gasVolumeL)} L at 25 °C, 100 kPa</dd>
              ) : null}
            </div>
          </dl>
        </figure>
        <div className="min-w-0 space-y-4">
          <ModelInputPanel
            legend="Set the power pack"
            fields={ELECTROLYSIS_FIELDS}
            values={setup}
            onChange={(next) => setSetup(next as ElectrolysisSetup)}
            sliderKey="currentA"
          />
          <ExploreChallenges
            question={FARADAY_QUESTION}
            challenges={FARADAY_CHALLENGES}
            result={{ productId: item.cathode.productId, currentA: setup.currentA, result }}
          />
          <WorkedStepsPanel
            title="How the model works it out"
            steps={result.steps}
            stepTestIdPrefix="faraday-step"
            result={result.result}
            resultTestId="faraday-result"
            limitNote={result.limitNote}
          />
          <p className="text-xs font-bold leading-5 text-slate2">
            Assumes all of the charge forms this product (100% current efficiency).
            {result.gasVolumeL !== null ? ` Gas volume uses ${MOLAR_GAS_VOLUME_L_PER_MOL} L/mol for an ideal gas at 25 °C and 100 kPa.` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
